import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./Header.css";
import logo from "../../pages/assets/LogoSofaF.png";
import { useTranslation } from "react-i18next";
import { useLang } from "../../LanguageProvider";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  
  // Global language controller (Header is the single source of truth)
  const { lang, setLang } = useLang();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onResize = () => window.innerWidth > 860 && setOpen(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <header
      className={`header ${scrolled ? "is-scrolled" : ""}`}
      data-open={open ? "true" : "false"}
    >
      <div className="container header__inner">
        {/* Logo */}
        <Link to="/" className="brand" aria-label={t("header.aria.goHome")}>
          <img
            src={logo}
            alt={t("header.brandAlt")}
            className="brand__logo"
            width="88"
            height="88"
            loading="eager"
          />
        </Link>

        {/* Mobile toggle */}
        <button
          className="nav__toggle"
          aria-label={
            open ? t("header.aria.closeMenu") : t("header.aria.openMenu")
          }
          aria-expanded={open}
          aria-controls="primary-navigation"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="bar" />
          <span className="bar" />
          <span className="bar" />
        </button>

        {/* Nav */}
        <nav
          id="primary-navigation"
          className={`nav ${open ? "is-open" : ""}`}
          role="navigation"
          aria-label={t("header.aria.mainNav")}
        >
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `nav__link ${isActive ? "active" : ""}`
            }
          >
            {t("header.nav.home")}
          </NavLink>

          <NavLink
            to="/products"
            className={({ isActive }) =>
              `nav__link ${isActive ? "active" : ""}`
            }
          >
            {t("header.nav.products")}
          </NavLink>

          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `nav__link ${isActive ? "active" : ""}`
            }
          >
            {t("header.nav.contact")}
          </NavLink>
        </nav>

        <div
          className="lang-switch"
          aria-label={t("header.aria.languageSwitcher")}
        >
          <button
            type="button"
            onClick={() => setLang("es")}
            className={`lang-btn ${lang === "es" ? "is-active" : ""}`}
            aria-pressed={lang === "es"}
            aria-label={t("header.lang.esLabel")}
            title={t("header.lang.esLabel")}
          >
            <svg viewBox="0 0 30 20" className="flag-svg" aria-hidden="true">
              <rect width="30" height="20" fill="#002B7F" />
              <rect y="3" width="30" height="14" fill="#ffffff" />
              <rect y="6" width="30" height="8" fill="#CE1126" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => setLang("en")}
            className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
            aria-pressed={lang === "en"}
            aria-label={t("header.lang.enLabel")}
            title={t("header.lang.enLabel")}
          >
            <svg viewBox="0 0 30 20" className="flag-svg" aria-hidden="true">
              <rect width="30" height="20" fill="#B22234" />
              {[...Array(7)].map((_, i) => (
                <rect
                  key={i}
                  y={i * 3}
                  width="30"
                  height="1.5"
                  fill="#ffffff"
                />
              ))}
              <rect width="12" height="9" fill="#3C3B6E" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
